// BookingSummary.js - Extracted from MonthCalendarView
import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Button,
    CircularProgress,
    useTheme,
    Select,
    MenuItem,
    FormControl,
    InputLabel
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import dayjs from 'dayjs';
import { getCommunitiesForMember } from 'api';

const BookingSummary = ({
    selectedPrice,
    selectedDate,
    spaces,
    totalPrice,
    totalPriceVAT,
    preparingBooking,
    priceDataLoading,
    doBooking,
    bookingType,
    selectedCommunity,
    onCommunityChange
}) => {
    const theme = useTheme();
    const [userCommunities, setUserCommunities] = useState([]);
    const [loadingCommunities, setLoadingCommunities] = useState(false);

    // Fetch user's communities (only active memberships)
    useEffect(() => {
        const fetchCommunities = async () => {
            try {
                setLoadingCommunities(true);
                const response = await getCommunitiesForMember();
                if (response?.communities) {
                    // Filter to only show communities where user has active membership
                    const activeCommunities = response.communities.filter(
                        membership => membership.status === 'AC' && membership.group
                    );
                    setUserCommunities(activeCommunities);
                }
            } catch (error) {
                console.error('Error fetching communities:', error);
            } finally {
                setLoadingCommunities(false);
            }
        };
        
        fetchCommunities();
    }, []);


    const calculateActualPrice = () => {
        if (!selectedPrice) return { basePrice: 0, priceWithVAT: 0 };

        const basePrice = parseFloat(selectedPrice.price) * spaces;
        const priceWithVAT = parseFloat(selectedPrice.price_vat) * spaces;

        return { basePrice, priceWithVAT };
    };

    const { basePrice, priceWithVAT } = calculateActualPrice();
    // Calculate Stripe fee
    const calculateStripeFee = (priceWithVAT) => {
        // UK card rate: 1.4% + £0.20
        const ukCardFee = Math.max(
            priceWithVAT * 0.014 + 0.20,  // Percentage fee + fixed fee
            0.20  // Minimum fee
        );

        // Round to nearest 5p for simplicity
        return Math.ceil(ukCardFee * 20) / 20;
    };

    // Helper to determine the booking status message
    const getBookingStatusMessage = () => {
        // During price data loading, show loading message
        if (priceDataLoading) {
            return "Loading availability...";
        }

        if (selectedDate) {
            if (dayjs(selectedDate).isBefore(dayjs(), 'day')) {
                return 'Cannot book dates in the past';
            } else if (selectedPrice) {
                if (selectedPrice.available < spaces) {
                    return 'Not enough spaces available';
                } else if (parseFloat(selectedPrice.price) <= 0) {
                    return 'No spaces available for this date';
                } else {
                    return 'Ready to book';
                }
            } else {
                return 'This date is not available for booking';
            }
        } else {
            return 'Please select a date';
        }
    };

    // Generate title based on booking type
    const getBookingTitle = () => {
        if (bookingType === 'MR') {
            return "Meeting Room";
        } else {
            return `${spaces} ${spaces > 1 ? 'Hot Desks' : 'Hot Desk'}`;
        }
    };

    return (
        <Box className="booking-summary-container">
            {selectedPrice?.available >= spaces && parseFloat(selectedPrice?.price) > 0 ? (
                <>
                    {/* Processing fee section */}
                    <Box sx={{
                        mb: 1,
                        px: 2,
                        py: 0.5,
                        borderRadius: 1,
                        backgroundColor: 'action.hover',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}>
                        <Typography variant="body2" color="text.secondary">
                            Processing fee
                        </Typography>
                        <Box sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-end'
                        }}>
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1
                            }}>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        textDecoration: 'line-through',
                                        color: 'text.secondary'
                                    }}
                                >
                                    {calculateStripeFee(totalPriceVAT).toLocaleString('en-GB', {
                                        style: 'currency',
                                        currency: 'GBP',
                                    })}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    color="primary.main"
                                    fontWeight={600}
                                >
                                    £0.00
                                </Typography>
                            </Box>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem', mt: -0.5 }}>
                                VAT inc
                            </Typography>
                        </Box>
                    </Box>

                    {/* Share with Community Selector */}
                    {onCommunityChange && userCommunities.length > 0 && (
                        <Box sx={{ mb: 1.5, px: 2 }}>
                            <FormControl fullWidth size="small">
                                <InputLabel id="community-select-label">
                                    <Typography variant="caption">Share with community (optional)</Typography>
                                </InputLabel>
                                <Select
                                    labelId="community-select-label"
                                    value={selectedCommunity || ''}
                                    onChange={(e) => onCommunityChange(e.target.value)}
                                    label="Share with community (optional)"
                                    disabled={loadingCommunities}
                                    sx={{ fontSize: '0.875rem' }}
                                >
                                    <MenuItem value="">
                                        <em>Don't share</em>
                                    </MenuItem>
                                    {userCommunities.map((membership) => (
                                        <MenuItem key={membership.group.slug} value={membership.group.slug}>
                                            {membership.group.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>
                    )}

                    {/* Main summary box */}
                    <Box sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        p: 2,
                        backgroundColor: 'background.paper',
                        borderRadius: 1,
                        boxShadow: 1
                    }}>
                        <Box>
                            <Typography variant="subtitle1" fontWeight={500}>
                                {getBookingTitle()}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {selectedDate ? dayjs(selectedDate).format('DD MMM YYYY') : ''}
                            </Typography>
                        </Box>
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2
                        }}>
                            <Box sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'flex-end'
                            }}>
                                <Typography variant="h5" color="primary.main" fontWeight={600}>
                                    {basePrice.toLocaleString('en-GB', {
                                        style: 'currency',
                                        currency: 'GBP',
                                    })}
                                </Typography>
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{
                                        mt: -0.5,
                                        fontSize: '0.7rem'
                                    }}
                                >
                                    {priceWithVAT.toLocaleString('en-GB', {
                                        style: 'currency',
                                        currency: 'GBP',
                                    })} VAT inc.
                                </Typography>
                            </Box>
                            <Button
                                variant="contained"
                                color="button_color"
                                onClick={doBooking}
                                disabled={preparingBooking}
                                sx={{
                                    minWidth: { xs: '80px', sm: '100px' }
                                }}
                            >
                                {preparingBooking ? <CircularProgress size={20} /> : 'Confirm'}
                            </Button>
                        </Box>
                    </Box>

                    {/* Booking confirmation notice */}
                    {selectedPrice && parseFloat(selectedPrice.price) > 0 && !selectedPrice.instant_booking && (
                        <Box sx={{
                            mt: 1.5,
                            p: 1.5,
                            borderRadius: 1,
                            backgroundColor: 'action.hover',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1
                        }}>
                            <Box sx={{
                                fontSize: '1.2rem',
                                color: theme.palette.info.main,
                                display: 'flex',
                                alignItems: 'center'
                            }}>
                                <InfoIcon fontSize="inherit" />
                            </Box>
                            <Typography variant="body2" color="text.secondary">
                                Your booking will need to be confirmed by the host. You'll receive notification once it's approved, typically within 24 hours.
                            </Typography>
                        </Box>
                    )}
                </>
            ) : (
                <Box className="booking-status">
                    {priceDataLoading ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
                            <CircularProgress size={24} sx={{ mr: 2 }} />
                            <Typography variant="body1">
                                Checking availability...
                            </Typography>
                        </Box>
                    ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
                            <Typography variant="body1">
                                {getBookingStatusMessage()}
                            </Typography>
                        </Box>
                    )}
                </Box>
            )}
        </Box>
    );
};

export default BookingSummary;